import axios, { AxiosInstance, AxiosResponse } from "axios";
import { FeedbackFormData } from "../Pages/Dashboard/ContactUsForm";
import SessionManager from "./session";

class HttpClient {
  private static instance: HttpClient;
  private _backendUrl: string;
  private _axiosInstance: AxiosInstance;

  private constructor(backendUrl: string) {
    this._backendUrl = backendUrl;
    this._axiosInstance = axios.create({
      baseURL: this.backendUrl,
      withCredentials: true,
      // Reject absolute URLs so requests can never bypass baseURL (SSRF hardening).
      allowAbsoluteUrls: false,
    });

    this._axiosInstance.interceptors.request.use((config) => {
      const sessionId = SessionManager.getInstance().getSessionId();

      config.headers.set("X-Session-Id", sessionId);

      return config;
    });
  }

  public get backendUrl(): string {
    return this._backendUrl;
  }

  public get axiosInstance(): AxiosInstance {
    return this._axiosInstance;
  }

  public static getInstance(backendUrl: string): HttpClient {
    if (!HttpClient.instance) {
      HttpClient.instance = new HttpClient(backendUrl);
    }
    return HttpClient.instance;
  }

  private retrieveConfig() {
    let config = undefined;
    if (sessionStorage.getItem("accessToken")) {
      config = {
        headers: {
          "Content-Type": "application/json",
          "Access-Token": sessionStorage.getItem("accessToken") || "",
        },
      };
    }
    return config;
  }

  public async get<T>(
    url: string,
    params: any = null
  ): Promise<AxiosResponse<T>> {
    if (params) {
      return this._axiosInstance.get<T>(url, { params });
    }
    return this._axiosInstance.get<T>(url, this.retrieveConfig());
  }

  /**
   * SSRF mitigation for POST requests (see also `allowAbsoluteUrls: false` above).
   * `url` is always a hardcoded, developer-supplied route literal from this file
   * (e.g. "api/v1/solution/addSolution") — never derived from user input, query
   * params, or response data — so there is no attacker-controlled path here.
   * This validation is defense-in-depth on top of that: it rejects absolute URLs,
   * protocol-relative URLs ("//host/..."), any URL with a scheme (http:, file:,
   * javascript:, etc.), control characters/backslashes, and credentials embedded
   * in the URL, then confirms the final resolved URL still shares the exact same
   * origin as the configured backend before allowing the request through.
   */
  // Detects control characters (0x00-0x1F, 0x7F) or backslashes without using a
  // control-character regex literal
  private hasControlCharOrBackslash(value: string): boolean {
    for (let i = 0; i < value.length; i++) {
      const code = value.charCodeAt(i);
      if (code <= 0x1f || code === 0x7f || value[i] === "\\") {
        return true;
      }
    }
    return false;
  }

  private resolvePostUrl(url: string): string {
    const invalidUrlError = new TypeError("Invalid POST request URL");
    if (
      typeof url !== "string" ||
      !url ||
      url !== url.trim() ||
      this.hasControlCharOrBackslash(url) ||
      url.startsWith("//") ||
      /^[A-Za-z][A-Za-z\d+.-]*:/.test(url)
    ) {
      throw invalidUrlError;
    }

    try {
      const backendUrl = new URL(this._backendUrl);
      if (
        !["http:", "https:"].includes(backendUrl.protocol) ||
        backendUrl.username ||
        backendUrl.password
      ) {
        throw invalidUrlError;
      }

      const resolvedUrl = new URL(url, backendUrl);
      if (
        !["http:", "https:"].includes(resolvedUrl.protocol) ||
        resolvedUrl.username ||
        resolvedUrl.password ||
        resolvedUrl.origin !== backendUrl.origin
      ) {
        throw invalidUrlError;
      }

      return url;
    } catch (error) {
      if (error === invalidUrlError) {
        throw error;
      }
      throw invalidUrlError;
    }
  }

  public async post<T>(url: string, data: any): Promise<AxiosResponse<T>> {
    const resolvedUrl = this.resolvePostUrl(url);
    return this._axiosInstance.post<T>(resolvedUrl, data, this.retrieveConfig());
  }

  public async put<T>(url: string, data: any): Promise<AxiosResponse<T>> {
    return this._axiosInstance.put<T>(url, data, this.retrieveConfig());
  }

  public async delete<T>(url: string, data: any = null): Promise<AxiosResponse<T>> {
    return this._axiosInstance.delete<T>(url, { ...this.retrieveConfig(), data });
  }
}

export default HttpClient;

// ===============================================================================
// Core / shared types
// ===============================================================================
// Fields shared verbatim between the app-level
// Solution interface and the publisher-side PublisherSolution interface below.
export interface SolutionCommon {
  required_peripherals?: string[];
  optional_peripherals?: string[];
  review_status_valid?: boolean;
  review_notes?: string;
  jira_ticket?: string;
  headset_fw_v?: string;
  device_fw_v?: string;
}

// Publisher-specific solution shape (matches compsolutionmodel.Solution from Go DB layer).
export interface PublisherSolution extends SolutionCommon {
  id?: string;
  note?: string;
  recommend?: boolean;
  product_id?: string;
  device_id?: string;
  compat_status?: string;
  source?: string;
  visibility?: boolean;
  identity_hash?: string;
  test_date?: string;
}

export interface Solution extends SolutionCommon {
  id: string;
  model_id: string;
  compat_status: string;
  created_at: string;
  device_id: string;
  note: string;
  product_id: string;
  recommend: boolean;
  region: string;
  visibility: boolean;
  source: string;
  required_peripherals_details?: Peripheral[];
  optional_peripherals_details?: Peripheral[];
  test_date?: Date | null;
  /** True for solutions derived from GetCertificateSolutions (cert-matched headsets). */
  isCertification?: boolean;
}

export interface Peripheral {
  image: any;
  id: string;
  name: string;
  sku: string;
  locale: any;
  created_at: any;
}

export interface AssociatedField {
  includeEOS?: boolean;
  id: string;
  batterytype?: string;
  boxcontents?: string;
  buttonusercontrols?: string;
  certifications?: string[] | string;
  connection: string;
  connectiontype: string[];
  connectsto: string[];
  created_at: string;
  feature_details: any;
  features: string[];
  headphonetype: string[];
  image: string;
  learn_url: string;
  locale: string[];
  locale_details: any;
  manufacturer: string;
  manufacturer_details: any;
  manufacturerwarranty: string;
  microphonetype: string[];
  model: string;
  model_details: any;
  name: string;
  paireddevices: string;
  primaryfeature: string;
  secondaryfeature: string;
  sku: string;
  speakersize: string;
  status: string;
  tcocertified?: string;
  wearingstyle: string;
  wirelessrange: string;
  recommend?: boolean;
  call_control_sdk?: string[];
  skus_by_model?: {
    learn_url: string;
    sku: string;
    long_name: string;
    isObsolete: boolean;
    boxcontents: string;
    required_peripherals?: string[];
    required_peripherals_details?: Peripheral[];
    optional_peripherals?: string[];
  }[];
  companions?: string[];
}

export interface FiltersData {
  Connection: DropdownOption[];
  ConnectsTo: DropdownOption[];
  WearingStyle: DropdownOption[];
  ConnectionType: DropdownOption[];
  HeadphoneType: DropdownOption[];
  Certifications: DropdownOption[];
}

export interface SolutionAssociation {
  associatedField: AssociatedField;
  solution: Solution;
}

export interface FilterData {
  id: string;
  category: string;
  name: string;
}

export interface Url {
  url: string;
}

// Fields shared between Region and RequestsCountByCountry.
export interface CountryLocaleInfo {
  countryCode: string;
  languageCode: string;
  countryName: string;
}

export interface User {
  id: string;
  email: string;
  role: string;
  last_login_at: string;
  user_status: string;
  created_at: string;
  updated_at: string;
}

// ===============================================================================
// Search
// ===============================================================================
export interface Suggestion {
  brand: string;
  category: string
  id: string;
  model_id: string;
  ids: string[];
  model: string;
  name: string;
  primary_feature: string;
  sku: string;
  source?: string;
}

interface SuggestionsResponse {
  end: number;
  results: Suggestion[];
  start: number;
  totalCount: number;
}

// interface searchSuggestionsResult {
//     message: string;
//     data: { suggestions: Suggestion[] };
// }

export const getSearchSuggestions = async (
  searchTerm: string,
  locale: string
): Promise<Suggestion[]> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  const response = await httpClient.get<SuggestionsResponse>(
    `api/v1/search/searchDevice?query=${encodeURIComponent(searchTerm)}&locale=${locale}`
  );
  return response.data.results;
};

// ===============================================================================
// Solutions
// ===============================================================================
export interface SolutionSummary {
  [key: string]: { [key: string]: number };
}

interface getSolutionsResult {
  message: string;
  data: { solutions: SolutionsByDeviceId };
}

interface SolutionsByDeviceId {
  [key: string]: {
    model_id?: string;
    solutions?: SolutionAssociation[];
    headsets?: any[];
  };
}

export const getSolutions = async (
  deviceID: Suggestion[],
  localeId: string,
  filter?: string,
  includeObsoleteProducts?: boolean,
  includeNotCompatible?: boolean,
): Promise<SolutionAssociation[]> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  const refs = deviceID.flatMap((device) =>
    device.ids.map((id) => ({ id, category: device.category }))
  );
  // Build a lookup so cert headset results use the same model_id as the parent Suggestion,
  // keeping it consistent with the tab filter in TabsContainer (device.model_id || device.id).
  const certModelIdByDeviceId = new Map<string, string>();
  deviceID.forEach((device) => {
    if (device.category?.toLowerCase() === 'certification') {
      device.ids.forEach((id) => certModelIdByDeviceId.set(id, device.model_id || device.id));
    }
  });
  let url = `api/v1/solution/getSolutionsById/${localeId}?refs=${encodeURIComponent(JSON.stringify(refs))}`;
  if (filter) {
    url += `&filters=${filter}`;
  }
  if (includeNotCompatible) {
    url += `&includeNotCompatible=${includeNotCompatible}`;
  }
  if (includeObsoleteProducts) {
    url += `&includeObsolete=${includeObsoleteProducts}`;
  }
  const response = await httpClient.get<getSolutionsResult>(url);
  const solutionsOfDeviceId = response.data.data.solutions;
  const keys = Object.keys(solutionsOfDeviceId);
  let only_solutions: SolutionAssociation[] = [];
  keys.forEach((key) => {
    const solutionData = solutionsOfDeviceId[key];

    if (solutionData.headsets) {
      // Certification device response: list of matching headset devices (no Solution objects).
      // Build synthetic SolutionAssociation entries so the rest of the pipeline works unchanged.
      solutionData.headsets.forEach((headset: any) => {
        const headsetId = (headset._id ?? headset.id)?.toString() ?? '';
        const isObsolete = headset.status === 'Obsolete';
        only_solutions.push({
          associatedField: {
            id: headsetId,
            name: headset.name ?? '',
            manufacturer: headset.manufacturer ?? '',
            manufacturer_details: headset.manufacturer_details,
            model: headset.model ?? '',
            model_details: headset.model_details,
            sku: headset.sku ?? '',
            connection: headset.connection ?? '',
            connectiontype: headset.connection_type ?? [],
            connectsto: headset.connects_to ?? [],
            wearingstyle: headset.wearing_style ?? '',
            headphonetype: headset.headphone_type ?? [],
            microphonetype: headset.microphone_type ?? [],
            primaryfeature: headset.primary_feature ?? '',
            secondaryfeature: headset.secondary_feature ?? '',
            wirelessrange: headset.wireless_range ?? '',
            manufacturerwarranty: headset.manufacturer_warranty ?? '',
            boxcontents: headset.box_contents ?? '',
            speakersize: headset.speaker_size ?? '',
            batterytype: headset.battery_type ?? '',
            paireddevices: headset.paired_devices ?? '',
            buttonusercontrols: headset.button_user_controls ?? '',
            image: headset.image ?? '',
            features: headset.features ?? [],
            learn_url: headset.learn_url ?? '',
            locale: headset.locale ?? [],
            locale_details: headset.locale_details,
            status: headset.status ?? '',
            tcocertified: headset.tco_certified ?? '',
            certifications: headset.certifications ?? [],
            call_control_sdk: headset.call_control_sdk ?? [],
            recommend: headset.recommend ?? false,
            created_at: headset.created_at ?? '',
            feature_details: headset.feature_details,
            // Build a single-entry skus_by_model so updateSolutionStatus works correctly.
            skus_by_model: [{
              sku: headset.sku ?? '',
              long_name: headset.name ?? '',
              isObsolete,
              learn_url: headset.learn_url ?? '',
              boxcontents: headset.box_contents ?? '',
            }],
          } as any,
          solution: {
            id: headsetId,
            model_id: certModelIdByDeviceId.get(key) ?? key,
            device_id: certModelIdByDeviceId.get(key) ?? key,
            product_id: headsetId,
            compat_status: 'Yes',
            note: '',
            recommend: headset.recommend ?? false,
            region: '',
            visibility: true,
            source: 'certification',
            created_at: '',
            isCertification: true,
          },
        });
      });
      return;
    }

    // Regular device response: standard { model_id, solutions: [...] } format.
    const data = (solutionData.solutions ?? []).map((x: any) => {
      return {
        ...x,
        solution: {
          ...x.solution,
          id: (x.solution as any)._id.toString(),
          model_id: solutionData.model_id || key,
        },
      };
    });
    only_solutions.push(...data);
  });
  return only_solutions;
};

export const updateSolutionById = async (solution: Solution): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.put<any>(
      `api/v1/solution/updateSolution`,
      { [solution.id]: solution }
    );
    await refreshCache();
    return response.data;
  } catch (error) {
    return null;
  }
};

export const updateMultipleSolutionsById = async (
  solutions: Solution[]
): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const newmap = solutions.reduce(
      (acc: Record<string, Solution>, solution) => {
        acc[solution.id] = solution;
        return acc;
      },
      {}
    );
    const response = await httpClient.put<any>(
      `api/v1/solution/updateSolution`,
      newmap
    );
    await refreshCache();
    return response.data;
  } catch (error) {
    return null;
  }
};

export const addNewSolution = async (solutions: Solution[]): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    // const solutionsAvailable = await findSolutionsByHeadsetIdAndDeviceId(
    //   solution.product_id,
    //   solution.device_id
    // );
    // if (solutionsAvailable.length > 0) {
    //   return null;
    // }

    const response = await httpClient.post<any>(
      `api/v1/solution/addSolution`,
      solutions
    );
    return response.data;
  } catch (error) {
    return null;
  }
};

export const deleteSolutionById = async (solutionId: string): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.delete<any>(
      `api/v1/solution/deleteSolution/${solutionId}`
    );
    return response.data;
  } catch (error) {
    return null;
  }
};

export const getSolutionsByCombinationOfProductsAndDevices = async (
  productIds?: string[],
  deviceIds?: string[],
  peripheralIds?: string[]
): Promise<Solution[]> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const queryParts: string[] = [];

    if (productIds?.length) {
      queryParts.push(...productIds.map((id) => `productids=${id}`));
    }
    if (deviceIds?.length) {
      queryParts.push(...deviceIds.map((id) => `deviceids=${id}`));
    }
    if (peripheralIds?.length) {
      queryParts.push(...peripheralIds.map((id) => `peripheralids=${id}`));
    }

    const query = queryParts.join("&");
    const response = await httpClient.get<{
      message: string;
      data: { solutions: Solution[] };
    }>(
      `api/v1/solution/findSolsByProductDeviceAndPeripheralId?${query}`
    );
    return response.data.data.solutions;
  } catch (error) {
    return [];
  }
};

export const getSolutionCountByModel = async (): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.get<any>(
      `api/v1/solution/getSolutionCountByModel`
    );
    return response.data.data.solutions;
  } catch (error) {
    return {};
  }
};

export const getSolutionCountByManufacturer =
  async (): Promise<SolutionSummary> => {
    const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
    try {
      const response = await httpClient.get<any>(
        `api/v1/solution/getSolutionCountByManufacturer`
      );
      return response.data.data.solutions as SolutionSummary;
    } catch (error) {
      return {};
    }
  };

export const getSolutionSummary = async (): Promise<SolutionSummary> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.get<any>(
      `api/v1/solution/getSolutionSummary`
    );
    return response.data.data.solutions;
  } catch (error) {
    return {};
  }
};

export const getSolutionsByDeviceIds = async (deviceIds: string[]): Promise<Solution[]> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const query = deviceIds.map(id => `deviceids=${encodeURIComponent(id)}`).join('&');
    const response = await httpClient.get<{ data: { solutions: Solution[] } }>(
      `api/v1/solution/getSolutionsByDeviceIds?${query}`
    );
    return response.data.data.solutions;
  } catch (error) {
    return [];
  }
};

export const getSolutionsByPeripheralIds = async (peripheralIds: string[]): Promise<Solution[]> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const query = peripheralIds.map(id => `peripheralids=${encodeURIComponent(id)}`).join('&');
    const response = await httpClient.get<{ data: { solutions: Solution[] } }>(
      `api/v1/solution/getSolutionsByPeripheralIds?${query}`
    );
    return response.data.data.solutions;
  } catch (error) {
    return [];
  }
};

export const getSolutionsByProductIds = async (productIds: string[]): Promise<Solution[]> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const query = productIds.map(id => `productids=${encodeURIComponent(id)}`).join('&');
    const response = await httpClient.get<{ data: { solutions: Solution[] } }>(
      `api/v1/solution/getSolutionsByProductIds?${query}`
    );
    return response.data.data.solutions;
  } catch (error) {
    return [];
  }
};

export const findSolutionsByHeadsetIdAndDeviceId = async (
  headsetId: string,
  deviceId: string
): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.get<any>(
      `api/v1/solution/findSolutionByProductIdAndDeviceId?productid=${headsetId}&deviceid=${deviceId}`
    );
    if (!response?.data?.data?.solutions) {
      return [];
    }
    return response.data.data.solutions;
  } catch (error) {
    return [];
  }
};

export const copySolutionsFromDevice = async (
  fromDeviceId: string,
  toDeviceId: string
): Promise<CopySolutionsResponse> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  const url = `api/v1/solution/copySolutionsFromDevice`;

  try {
    const response = await httpClient.post<{ data: CopySolutionsResponse }>(url, { fromDeviceId, toDeviceId });
    return response.data.data;
  } catch (error) {
    return { count: 0, message: 'Failed to copy solutions' };
  }
};

export const copySolutionsFromProduct = async (
  fromProductId: string,
  toProductId: string,
  isCustomerSpecial?: boolean
): Promise<CopySolutionsResponse> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  const url = `api/v1/solution/copySolutionsFromProduct`;

  try {
    const response = await httpClient.post<{ data: CopySolutionsResponse }>(url, {
      fromProductId,
      toProductId,
      isCustomerSpecial: isCustomerSpecial ?? false,
    });
    return response.data.data;
  } catch (error) {
    return { count: 0, message: 'Failed to copy solutions' };
  }
};

// ===============================================================================
// Locale / Region
// ===============================================================================
export interface Region extends CountryLocaleInfo { }

export interface Locale {
  id: string;
  language: string[];
  country: string;
  country_name: string;
  region: string;
  culture_codes: string[];
}

export const getLocaleId = async (
  country: string,
  language: string = "EN"
): Promise<string> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.get<any>(
      `/api/v1/locale/getLocaleByCountryAndLanguage?country=${country}&language=${language}`
    );
    // Check if locale is default NIL value so we return empty string
    if (response.data.data.locale === "000000000000000000000000") {
      return "";
    }
    return response.data.data.locale;
  } catch (error) {
    return "";
  }
};

const extractSkuFromSolution = (solution: SolutionAssociation): string => {
  return solution.associatedField.sku;
};

export const listLocale = async (): Promise<Locale[]> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.get<{ data: { locales: Locale[] } }>(`api/v1/locale/listLocale`);
    return response.data.data.locales;
  } catch (error) {
    return [];
  }
};

// ===============================================================================
// Filters
// ===============================================================================
interface DropdownOption {
  label: string;
  value: string;
}

export const getFiltersByCategory = async (
  category: string
): Promise<FilterData[] | null> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.get<{
      message: string;
      data: { filters: FilterData[] };
    }>(`api/v1/filter/getFiltersByCategory/${category}`);
    return response.data.data.filters;
  } catch (error) {
    return null;
  }
};

export const getAllFilters = async (): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.get<{
      message: string;
      data: { filters: any };
    }>("api/v1/filter/getAllFilters");
    return response.data.data.filters;
  } catch (error) {
    return null;
  }
};

export const updateFilterById = async (
  filter: any
): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.put<any>(
      `api/v1/filter/updateFilter `,
      filter
    );
    await refreshCache();
    return response.data;
  } catch (error) {
    return null;
  }
};

export const addFilter = async (filters: { category: string; name: string }[]): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.post<any>(
      `api/v1/filter/addFilter`,
      filters
    );
    return response.data;
  } catch (error: any) {
    return null;
  }
};

export const getFilterUsage = async (
  localeIds: string[],
  startTime?: string
): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    let queryString = localeIds
      .map((id) => `locale=${encodeURIComponent(id)}`)
      .join("&");
    if (startTime) {
      queryString += (queryString ? "&" : "") + `startTime=${encodeURIComponent(startTime)}`;
    }
    const url = `api/v1/admin/analytics/getfilterusage?${queryString}`;
    const { data } = await httpClient.get<{
      data: { filterUsage: { filterUsage: any[] } };
    }>(url);
    return data.data.filterUsage;
  } catch (error) {
    return [];
  }
};

// ===============================================================================
// Devices / Models
// ===============================================================================
export interface ModelName {
  id: string;
  name: string;
}

export interface ModelTestStatus {
  name: string;
  testStatus: string;
  assumedFromName?: string;
  isVisible?: boolean;
}

export interface ModelsWithAllDevicesItHasSolutionsFor {
  [modelid: string]: {
    count: number;
    deviceid: string;
    deviceName: string;
    manufacturerid: string;
    manufacturerName: string;
  }[];
}

export interface NewHeadset {
  name: string;
  manufacturer: string;
  connection: string[];
  connection_type: string[];
  wearing_style: string[];
  connects_to: string[];
  headphone_type: string[];
  microphone_type: string[];
  image: string;
  primary_feature: string;
  secondary_feature: string;
  wireless_range: string;
  manufacturer_warranty: string;
  box_contents: string;
  speaker_size: string;
  battery_type: string;
  paired_devices: string;
  button_user_controls: string;
  model: string;
  sku: string;
  learn_url: string;
  locale: string[];
  status: string;
  category: string;
  assumed_from: string;
  end_of_sale_date: string | null;
  end_of_support_date: string | null;
  general_availability_date: string | null;
  product_announcement_date: string | null;
  selective_availability_date: string | null;
  test_status: string;
  tco_certified: string;
  certifications: string[];
  call_control_sdk: string[];
}

export const getBase64ImageFromSku = async (
  sku: string
): Promise<any | null> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);

  try {
    const encodedSku = encodeURIComponent(sku);
    const response = await httpClient.get<{
      data: { base64Images: { [key: string]: string } };
    }>(`api/v1/device/getBase64ImageFromSku?sku=${encodedSku}`);

    return { base64: response.data.data.base64Images[sku], sku };
  } catch (error) {
    return { base64: null, sku };
  }
};

export const findModelsByName = async (name: string): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  const response = await httpClient.get<any>(
    `api/v1/model/findModelByName?name=${name}`
  );
  if (!response?.data?.data?.models) {
    return [];
  }
  return response.data.data.models;
};

export const findModelById = async (modelid: string): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  const response = await httpClient.get<any>(
    `api/v1/model/getModel/${modelid}`
  );
  if (!response?.data?.data?.model) {
    return {};
  }
  return response.data.data.model;
};

export const findDevicesByModel = async (modelId: string): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  const response = await httpClient.get<any>(
    `api/v1/device/findDeviceByModelId/${modelId}`
  );
  if (!response?.data?.data?.device) {
    return [];
  }
  return response.data.data.device;
};

export const getDevicesByIdsWithAllData = async (
  ids: string[]
): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  const query = ids.map((id) => `id=${id}`).join("&");
  const response = await httpClient.get<any>(
    `api/v1/device/getDevicesByIdsWithAllData?${query}`
  );
  if (!response?.data?.data?.deviceData) {
    return [];
  }
  return response.data.data.deviceData;
};

export const findDevicesByName = async (name: string): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  const response = await httpClient.get<any>(
    `api/v1/device/findDeviceByNames?name=${name}`
  );
  if (!response?.data?.data?.device) {
    return [];
  }
  return response.data.data.device;
};

// Softphone functions disabled until feature is enabled — re-enable when ready
// const FALLBACK_SOFTPHONE_BRANDS = ["Google", "Microsoft", "Zoom"];

// Returns distinct softphone brands from the search API (e.g. Microsoft, Cisco, Google)
// export const getSoftphones = async (locale: string): Promise<{ label: string; value: string }[]> => {
//   try {
//     const terms = ["zoom", "microsoft", "google", "cisco"];
//     const resultsArrays = await Promise.all(
//       terms.map((t) => getSearchSuggestions(t, locale).catch(() => [] as Suggestion[]))
//     );
//     const allResults = resultsArrays.flat();
//     const brands = [
//       ...new Set(
//         allResults
//           .filter((r) => r.category?.toLowerCase() === "softphone")
//           .map((r) => r.brand)
//           .filter(Boolean)
//       ),
//     ];
//     if (brands.length > 0) {
//       return brands.map((b) => ({ label: b, value: b }));
//     }
//     return FALLBACK_SOFTPHONE_BRANDS.map((b) => ({ label: b, value: b }));
//   } catch {
//     return FALLBACK_SOFTPHONE_BRANDS.map((b) => ({ label: b, value: b }));
//   }
// };

// Returns softphone names for a given brand (e.g. "Microsoft Teams", "Microsoft Skype")
// export const getSoftphonesByBrand = async (
//   brand: string,
//   locale: string
// ): Promise<{ label: string; value: string }[]> => {
//   if (!brand) return [];
//   try {
//     const results = await getSearchSuggestions(brand, locale);
//     return results
//       .filter((r) => r.category?.toLowerCase() === "softphone" && r.brand === brand)
//       .map((r) => ({ label: r.name, value: r.name }));
//   } catch {
//     return [];
//   }
// };

export const updateHeadsetById = async (headset: any): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.put<any>(
      `api/v1/device/updateDevice`,
      headset
    );
    await refreshCache();
    return response.data;
  } catch (error) {
    return null;
  }
};

export const addNewHeadset = async (headset: NewHeadset[]): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.post<any>(
      `api/v1/device/addDevice`,
      headset
    );
    if (response.status !== 201) {
      throw new Error("Failed to add new headset");
    }
    await refreshCache();
    return response.data;
  } catch (error) {
    throw new Error("Failed to add new headset");
  }
};

export const updateModel = async (model: any): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.put<any>(
      `api/v1/model/updateModel/${model.id}`,
      model
    );
    await refreshCache();
    return response.data;
  } catch (error) {
    return null;
  }
};

export const getAllModels = async (): Promise<ModelName[]> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.get<{ data: { models: ModelName[] } }>(`api/v1/model/fetchmodelnames`);
    return response.data.data.models;
  } catch (error) {
    return [];
  }
};

export const listModels = async (): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.get<any>(`api/v1/model/listModel`);
    return response.data.data.models;
  } catch (error) {
    return [];
  }
};

export const updateDeviceById = async (device: any): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.put<any>(
      `api/v1/device/updateDevice`,
      device
    );
    await refreshCache();
    return response.data;
  } catch (error) {
    return null;
  }
};

export const addNewDevice = async (device: any[]): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.post<any>(
      `api/v1/device/addDevice`,
      device
    );
    return response.data;
  } catch (error) {
    return null;
  }
};

export const getModelsWithTestStatus = async (): Promise<ModelTestStatus[]> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.get<{
      data: { modelData: ModelTestStatus[] };
    }>(`api/v1/model/getModelsWithTestStatusAndCounts`);
    return response.data.data.modelData;
  } catch (error) {
    return [];
  }
};

export const getDevicesWithTestStatus = async (): Promise<ModelTestStatus[]> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.get<{
      data: { deviceData: ModelTestStatus[] };
    }>(`api/v1/device/getDevicesWithTestStatusAndCounts`);
    return response.data.data.deviceData;
  } catch (error) {
    return [];
  }
};

export const getModelsWithAllDevicesItHasSolutionsFor =
  async (): Promise<ModelsWithAllDevicesItHasSolutionsFor> => {
    const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
    try {
      const response = await httpClient.get<{
        data: { solutions: ModelsWithAllDevicesItHasSolutionsFor };
      }>(`api/v1/solution/getModelsWithAllDevicesItHasSolutionsFor`);
      return response.data.data.solutions;
    } catch (error) {
      return {};
    }
  };

export const getHeadsetModelWithSKUAndDates = async (): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const url = `api/v1/device/getHeadsetModelWithSKUAndDates`;
    const response = await httpClient.get<any>(url);
    return response.data.data;
  } catch (error) {
    return [];
  }
};

export const fetchModelNamesWithDetails = async (): Promise<any[]> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.get<any>(
      `api/v1/model/fetchmodelnameswithdetails`
    );
    return response.data.data.models.filter(
      (x: any) =>
        x.accessories_details.filter((y: any) => Object.entries(y).length > 0)
          .length > 0
    );
  } catch (error) {
    return [];
  }
};

export const getHeadsetsCountByModel = async (): Promise<SolutionSummary> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.get<any>(
      `api/v1/device/getHeadsetsCountByModel`
    );
    return response.data.data.devices;
  } catch (error) {
    return {};
  }
};

export const getHeadsetDeskphoneData = async (): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const url = `api/v1/device/fetchHeadsetDeskphoneData`;
    const response = await httpClient.get<any>(url);
    return response.data.data;
  } catch (error) {
    return [];
  }
};

// ===============================================================================
// Manufacturers
// ===============================================================================
export interface Manufacturer {
  id: string;
  name: string;
  brand: string;
  homepage: string;
}

export const findManufacturersByName = async (name: string): Promise<Manufacturer[]> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  const response = await httpClient.get<any>(
    `api/v1/manufacturer/findManufacturerByName?name=${encodeURIComponent(name)}`
  );
  try {
    if (!response?.data?.data?.manufacturers) {
      return [];
    }
    return response.data.data.manufacturers;
  } catch (error) {
    return [];
  }
};

export const findDevicesByManufacturer = async (
  manufacturerId: string
): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  const response = await httpClient.get<any>(
    `api/v1/device/getDevicesByManufacturer?manufacturer=${manufacturerId}`
  );
  if (!response?.data?.data?.devices) {
    return [];
  }
  return response.data.data.devices;
};

export const updateManufacturerById = async (
  manufacturer: any
): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.put<any>(
      `api/v1/manufacturer/updateManufacturer`,
      manufacturer
    );
    await refreshCache();
    return response.data;
  } catch (error) {
    return null;
  }
};

export const addManufacturer = async (manufacturers: { name: string; brand: string; homepage?: string }[]): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.post<any>(
      `api/v1/manufacturer/addManufacturer`,
      manufacturers
    );
    return response.data;
  } catch (error) {
    return null;
  }
};

export const getManufacturerById = async (
  manufacturerId: string
): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.get<any>(
      `api/v1/manufacturer/getManufacturer/${manufacturerId}`
    );
    return response.data.data.manufacturer;
  } catch (error) {
    return null;
  }
};

export const getAllManufacturers = async (): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.get<any>(
      `api/v1/manufacturer/listManufacturer?limit=500`
    );
    return response.data.data.manufacturers;
  } catch (error) {
    return [];
  }
};

export const getDevicesCountByManufacturer =
  async (): Promise<SolutionSummary> => {
    const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
    try {
      const response = await httpClient.get<any>(
        `api/v1/device/getDevicesCountByManufacturer`
      );
      return response.data.data.devices;
    } catch (error) {
      return {};
    }
  };

export const getManufacturerDeviceStatsBySource = async (): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const url = `api/v1/device/getManufacturerDeviceStatsBySource`;
    const response = await httpClient.get<any>(url);
    return response.data.data.stats;
  } catch (error) {
    return [];
  }
};

export const searchDevicesByManufacturerName = async (
  name: string
): Promise<any> => {
  const data = await findManufacturersByName(name);
  if (data.length === 0) {
    return [];
  }
  const [manufacturer] = data;
  const devices = await findDevicesByManufacturer(manufacturer.id);
  return devices;
};

// ===============================================================================
// Peripherals
// ===============================================================================
export interface PeripheralCountSeparator {
  new: number;
  legacy: number;
}

export interface PeripheralAssociation {
  peripheralName: string;
  peripheralId: string;
  headsetModelCount: number;
  headsetModelNames: string[];
  requiredCount: PeripheralCountSeparator;
  optionalCount: PeripheralCountSeparator;
}

export const getListOfPeripherals = async (): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    // for now count is set arbitrarily to 100 to get all peripherals
    const response = await httpClient.get<any>(
      `api/v1/peripheral/listPeripheral?count=100`
    );
    return response.data.data.peripherals;
  } catch (error) {
    return [];
  }
};

export const fetchModelsTestedWithGivenAccessory = async (
  peripheralIds: string[]
): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const query = peripheralIds.map((id) => `id=${id}`).join("&");
    const response = await httpClient.get<any>(
      `api/v1/model/fetchmodelstestedwithgivenaccessory?${query}`
    );
    return response.data.data.models;
  } catch (error) {
    return [];
  }
};

export const getPeripheralById = async (peripheralId: string): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.get<any>(
      `api/v1/peripheral/getPeripheral/${peripheralId}`
    );
    return response.data.data.peripheral;
  } catch (error) {
    return null;
  }
};

export const getPeripheralsList = async (): Promise<Peripheral[]> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.get<{ data: { peripherals: Peripheral[] } }>(
      `api/v1/peripheral/listPeripheral?count=500`
    );
    return response.data.data.peripherals;
  } catch (error) {
    return [];
  }
};

export const addNewPeripheral = async (peripherals: { name: string; sku: string; locale: string[] }[]): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.post<any>(
      `api/v1/peripheral/addPeripheral`,
      peripherals
    );
    return response.data;
  } catch (error) {
    return null;
  }
};

export const updatePeripheral = async (peripheral: { [key: string]: Peripheral }): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.put<any>(
      `api/v1/peripheral/updatePeripheral`,
      peripheral
    );
    return response.data;
  } catch (error) {
    return null;
  }
};

export const getPeripheralAssociationsWithCounts = async (): Promise<PeripheralAssociation[]> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.get<{
      data: { counts: { peripherals: PeripheralAssociation[] } };
    }>(`api/v1/solution/getPeripheralAssociationsWithCounts`);
    return response.data.data.counts.peripherals;
  } catch (error) {
    return [];
  }
};

// ===============================================================================
// Auth / Users
// ===============================================================================
export const introspectToken = async (): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.get<any>('introspect');
    return response;
  } catch (error) {
    return null;
  }
};

export const listAllUsers = async (): Promise<User[]> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.get<{ data: { users: User[] } }>(
      `api/v1/admin/user/getallusers`
    );
    return response.data.data.users;
  } catch (error) {
    return [];
  }
};

export const inviteUser = async (email: string, role: string): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.post<{ data: { message: string } }>(
      `api/v1/admin/user/inviteuser`,
      { email, role }
    );
    return response.data.data.message;
  } catch (error) {
    return null;
  }
};

export const updateUserStatus = async (
  email: string,
  status: string
): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.put<{ data: { message: string } }>(
      `api/v1/admin/user/updateuserstatus`,
      { email, status }
    );
    return response.data.data.message;
  } catch (error) {
    return null;
  }
};

export const updateUserRole = async (
  email: string,
  role: string
): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.put<{ data: { message: string } }>(
      `api/v1/admin/user/updateuserrole`,
      { email, role }
    );
    return response.data.data.message;
  } catch (error) {
    return null;
  }
};

export const deleteUser = async (email: string): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.delete<{ data: { message: string } }>(
      `api/v1/admin/user/deleteuser`,
      { email }
    );
    return response.data.data.message;
  } catch (error) {
    return null;
  }
};

export const getUserByEmail = async (email: string): Promise<User | null> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.post<{ data: { user: User } }>(
      `api/v1/admin/user/getuserbyemail`,
      { email }
    );
    return response.data.data.user;
  } catch (error) {
    return null;
  }
};

export const getUserRole = async (email: string): Promise<string | null> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.post<{ data: { role: string } }>(
      `api/v1/admin/user/getuserrole`,
      { email }
    );
    return response.data.data.role;
  } catch (error) {
    return null;
  }
};

export const getUserStatus = async (email: string): Promise<string | null> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.post<{ data: { status: string } }>(
      `api/v1/admin/user/getuserstatus`,
      { email }
    );
    return response.data.data.status;
  } catch (error) {
    return null;
  }
};

export const logout = async (): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.get<any>(`/logout`);
    return response.data;
  } catch (error) {
    return null;
  }
};

// ===============================================================================
// Analytics
// ===============================================================================
export interface RequestsCountByCountry extends CountryLocaleInfo {
  endpointCounts: Record<string, number>;
}

export const getCountryStatsForRequests = async (
  startTime?: string
): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    let url = `api/v1/admin/analytics/getcountrystatsforrequests`;
    if (startTime) {
      url += `?startTime=${encodeURIComponent(startTime)}`;
    }
    const response = await httpClient.get<{ data: { countryStats: any } }>(url);
    return response.data.data.countryStats;
  } catch (error) {
    return null;
  }
};

export const analyticsGetRequestDataByLocale = async (
  startTime?: string
): Promise<RequestsCountByCountry[]> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    let url = `api/v1/admin/analytics/getRequestDataByLocale`;
    if (startTime) {
      url += `?startTime=${encodeURIComponent(startTime)}`;
    }
    const response = await httpClient.get<any>(url);
    return response.data.data.results;
  } catch (error) {
    return [];
  }
};

export const getAnalyticsUserInteractions = async (
  localeIds: string[],
  startTime?: string
): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    let queryString = localeIds
      .map((id) => `locale=${encodeURIComponent(id)}`)
      .join("&");
    if (startTime) {
      queryString += (queryString ? "&" : "") + `startTime=${encodeURIComponent(startTime)}`;
    }
    const url = `api/v1/admin/analytics/getuserinteractionactioncounts?${queryString}`;
    const response = await httpClient.get<{ data: { actionCounts: any } }>(url);
    return response.data.data.actionCounts;
  } catch (error) {
    return null;
  }
};

export const getAnalyticsSearchStatsByLocale = async (
  localeIds: string[],
  startTime?: string
): Promise<any[]> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    let queryString = localeIds
      .map((id) => `locale=${encodeURIComponent(id)}`)
      .join("&");
    if (startTime) {
      queryString += (queryString ? "&" : "") + `startTime=${encodeURIComponent(startTime)}`;
    }
    const url = `api/v1/admin/analytics/getsearchstatsperlocale${queryString ? `?${queryString}` : ''}`;
    const response = await httpClient.get<{ data: { results: any[] } }>(url);
    return response.data.data.results;
  } catch (error) {
    return [];
  }
};

export const getHeadsetModelFeatureUsage = async (
  localeIds: string[],
  startTime?: string
): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    let queryString = localeIds
      .map((id) => `locale=${encodeURIComponent(id)}`)
      .join("&");
    if (startTime) {
      queryString += (queryString ? "&" : "") + `startTime=${encodeURIComponent(startTime)}`;
    }
    const url = `api/v1/admin/analytics/getinteractioncountsbyheadsetmodel?${queryString}`;
    const { data } = await httpClient.get<{
      data: { actionCounts: any[] };
    }>(url);
    return data.data.actionCounts;
  } catch (error) {
    return [];
  }
};

export interface SkuCountriesData {
  _id: string;
  category: string;
  locales: {
    country: string;
    country_name: string;
  }[];
  model: string;
  name: string;
  sku: string;
}

export const getHeadsetsWithCountriesData = async (): Promise<SkuCountriesData[]> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const url = `api/v1/device/listHeadsetsWithCountries`;
    const response = await httpClient.get<any>(url);
    return response.data.data.devices;
  } catch (error) {
    return [];
  }
};

export interface HeadsetModelStatistics {
  [key: string]: {
    [key: string]: PLCStatusCounts
  };
}

interface PLCStatusCounts {
  [key: string]: {
    count: number;
    skus: string[];
  }
}

export const getHeadsetModelStatsSkuLevel = async (sources?: string[]): Promise<HeadsetModelStatistics> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    let url = `api/v1/device/getHeadsetModelStatsSkuLevel`;
    if (sources && sources.length > 0) {
      const sourceParams = sources.map(source => `source=${encodeURIComponent(source)}`).join('&');
      url += `?${sourceParams}`;
    }
    const response = await httpClient.get<any>(url);
    return response.data.data.stats;
  } catch (error) {
    return {};
  }
};

export interface SkuURLHealth {
  SKU: string;
  Image: string;
  LearnURL: string;
  Status: string;
  ImageHealth: string;
  LearnHealth: string;
}

export interface SkuURLHealthResponse {
  ResultData: SkuURLHealth[];
  LastChecked: Date | null;
}

export const getSkuURLHealthStats = async (): Promise<SkuURLHealthResponse> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.get<{
      data: { results: { ResultData: SkuURLHealth[], LastChecked: string | null } };
    }>(`api/v1/device/getSkuWithImageAndLearnLinks`);
    return {
      ResultData: response.data.data.results.ResultData,
      LastChecked: response.data.data.results.LastChecked ? new Date(response.data.data.results.LastChecked) : null
    };
  } catch (error) {
    return { ResultData: [], LastChecked: null };
  }
};

export const getFeedbackTypeStatsPerLocale = async (startTime?: string): Promise<any[]> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    let url = `api/v1/admin/analytics/getfeedbacktypestatsperlocale`;
    if (startTime) {
      url += `?startTime=${encodeURIComponent(startTime)}`;
    }
    const response = await httpClient.get<any>(url);
    return response.data.data.countryStats;
  } catch (error) {
    return [];
  }
};

export const getFeedbackCombinationationStats = async (
  localeIds: string[],
  startTime?: string
): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    let queryString = localeIds
      .map((id) => `locale=${encodeURIComponent(id)}`)
      .join("&");
    if (startTime) {
      queryString += (queryString ? "&" : "") + `startTime=${encodeURIComponent(startTime)}`;
    }
    const url = `api/v1/admin/analytics/getfeedbackcombinationcounts?${queryString}`;
    const { data } = await httpClient.get<{
      data: { feedbackCombinationStats: any[] };
    }>(url);
    return data.data.feedbackCombinationStats;
  } catch (error) {
    return [];
  }
};

export const getNoResponseSearchTerms = async (
  localeIds: string[],
  startTime?: string
): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    let queryString = localeIds
      .map((id) => `locale=${encodeURIComponent(id)}`)
      .join("&");
    if (startTime) {
      queryString += (queryString ? "&" : "") + `startTime=${encodeURIComponent(startTime)}`;
    }
    const url = `api/v1/admin/analytics/getnoresponsesearchterms?${queryString}`;
    const { data } = await httpClient.get<{
      data: { data: any[] };
    }>(url);
    return data.data.data;
  } catch (error) {
    return [];
  }
};

export const getNoResultSearchTermsByReason = async (
  localeIds: string[],
  startTime?: string,
  noResultReason?: string
): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    let queryString = localeIds
      .map((id) => `locale=${encodeURIComponent(id)}`)
      .join("&");
    if (startTime) {
      queryString += (queryString ? "&" : "") + `startTime=${encodeURIComponent(startTime)}`;
    }
    if (noResultReason) {
      queryString += (queryString ? "&" : "") + `noResultReason=${encodeURIComponent(noResultReason)}`;
    }
    const url = `api/v1/admin/analytics/getnoresultsearchtermsbyreason?${queryString}`;
    const { data } = await httpClient.get<{
      data: { data: any[] };
    }>(url);
    return data.data.data;
  } catch (error) {
    return [];
  }
};

export const getWeeklyVisitTrends = async (
  startTime?: string
): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    let url = `api/v1/admin/analytics/getweeklyvisittrends`;
    if (startTime) {
      url += `?startTime=${encodeURIComponent(startTime)}`;
    }
    const response = await httpClient.get<{ data: { WeeklyVisits: any } }>(url);
    return response.data.data.WeeklyVisits;
  } catch (error) {
    return null;
  }
};

export interface HeadsetModelCompatibilityStats {
  headset_model_id: string;
  status_details: StatusDetail[];
  total_distinct: number;
}

export interface StatusDetail {
  status: string;
  count: number;
  device_ids: string[];
}

export const getHeadsetModelCompatibilityStats = async (
  reviewStatusValid?: boolean,
  source?: string
): Promise<HeadsetModelCompatibilityStats[]> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    let url = `api/v1/solution/getHeadsetModelCompatibilityStats`;
    const params: string[] = [];

    if (reviewStatusValid !== undefined) {
      params.push(`reviewStatusValid=${reviewStatusValid}`);
    }
    if (source !== undefined) {
      params.push(`source=${encodeURIComponent(source)}`);
    }

    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }

    const response = await httpClient.get<{
      data: { results: HeadsetModelCompatibilityStats[] };
    }>(url);
    return response.data.data.results;
  } catch (error) {
    return [];
  }
};

export interface ManufacturerCompatibilityDetail {
  manufacturer_id: string;
  status_details: StatusDetail[];
  total_distinct: number;
}

export interface HeadsetManufacturerCompatibilityStats {
  headset_model_id: string;
  manufacturers: ManufacturerCompatibilityDetail[];
  total_distinct_all_manufacturers: number;
}

export const getHeadsetManufacturerCompatibilityStats = async (
  reviewStatusValid?: boolean,
  source?: string
): Promise<HeadsetManufacturerCompatibilityStats[]> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    let url = `api/v1/solution/getHeadsetManufacturerCompatibilityStats`;
    const params: string[] = [];

    if (reviewStatusValid !== undefined) {
      params.push(`reviewStatusValid=${reviewStatusValid}`);
    }
    if (source !== undefined) {
      params.push(`source=${encodeURIComponent(source)}`);
    }

    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }

    const response = await httpClient.get<{
      data: { results: HeadsetManufacturerCompatibilityStats[] };
    }>(url);
    return response.data.data.results;
  } catch (error) {
    return [];
  }
};

// ===============================================================================
// Feedback
// ===============================================================================
export const newFeedback = async (payload: FeedbackFormData): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  const response = await httpClient.get<any>(
    "api/v1/feedback/newfeedback",
    payload
  );
  if (!response?.data?.data?.device) {
    return [];
  }
  return response.data.data.device;
};

// ===============================================================================
// Hermes catalog
// ===============================================================================
export interface HierarchyNode {
  hierarchyId: string;
  hierarchyName: string;
  productNumber: string;
  inputHierarchyId: string;
  hierarchyPath: string;
}

export const getHermesSubCategories = async (): Promise<HierarchyNode[]> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.get<{ data: { data: { hierarchyNodes: { [key: string]: HierarchyNode } } } }>(`api/v1/solution/getHermesSubCategories`);
    return Object.values(response.data.data.data.hierarchyNodes);
  } catch (error) {
    return [];
  }
};

export const getHermesBigSeries = async (hierarchyIds: string[]): Promise<HierarchyNode[]> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.get<{ data: { data: { hierarchyNodes: { [key: string]: HierarchyNode } } } }>(`api/v1/solution/getHermesBigSeries?hierarchyIds=${hierarchyIds.join(',')}`);
    return Object.values(response.data.data.data.hierarchyNodes);
  } catch (error) {
    return [];
  }
};

export const getHermesSmallSeries = async (hierarchyIds: string[]): Promise<HierarchyNode[]> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.get<{ data: { data: { hierarchyNodes: { [key: string]: HierarchyNode } } } }>(`api/v1/solution/getHermesSmallSeries?hierarchyIds=${hierarchyIds.join(',')}`);
    return Object.values(response.data.data.data.hierarchyNodes);
  } catch (error) {
    return [];
  }
};

export const getHermesModels = async (hierarchyIds: string[]): Promise<HierarchyNode[]> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.get<{ data: { data: { hierarchyNodes: { [key: string]: HierarchyNode } } } }>(`api/v1/solution/getHermesModels?hierarchyIds=${hierarchyIds.join(',')}`);
    return Object.values(response.data.data.data.hierarchyNodes);
  } catch (error) {
    return [];
  }
};

export const getHermesSkus = async (hierarchyIds: string[]): Promise<HierarchyNode[]> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.get<{ data: { data: { hierarchyNodes: { [key: string]: HierarchyNode } } } }>(`api/v1/solution/getHermesSkus?hierarchyIds=${hierarchyIds.join(',')}`);
    return Object.values(response.data.data.data.hierarchyNodes);
  } catch (error) {
    return [];
  }
};

export interface HermesSkuData {
  SKU: string;
  New_Model_Name: string;
  Name: string;
  Connection: string[];
  Connector: string[];
  Connects_To: string[];
  Headphone_Type: string[];
  Microphone_Type: string[];
  Wearing_Style: string[];
  PLC_Status: string;
  Wireless_Range: string;
  Manufacturer_Warranty: string;
  Box_Content: string;
  Speaker_Size: string;
  Battery_Type: string;
  Paired_Devices: string;
  Button_User_Control: string;
  Certified_Software_Collaborations: string[];
  Call_Control_SDK: string[];
  TCO: string;
  End_of_Sale: string;
  End_of_Support: string;
  General_Availability_Date: string;
  Product_Announcement_Date: string;
  Selective_Availability_Date: string;
  Image_URL: string;
  Learn_More_URL: string;
}

export const getHermesSkuData = async (sku: string): Promise<HermesSkuData[]> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.get<{ data: { data: { products: HermesSkuData[] } } }>(`api/v1/solution/getHermesData?skus=${sku}&country=WW&language=EN`);
    return response.data.data.data.products;
  } catch (error) {
    return [];
  }
};

export interface HeadsetSku {
  id: string;
  sku: string;
  name: string;
}

export const getHeadsetsSkus = async (): Promise<HeadsetSku[]> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.get<{ data: { devices: HeadsetSku[] } }>(`api/v1/device/listHeadsetsSkus`);
    return response.data.data.devices;
  } catch (error) {
    return [];
  }
};

export interface CompanionAccessory {
  name: string;
  number: string;
}

export const getHermesCompanionData = async (
  skus: string[],
  country: string,
  language: string
): Promise<Record<string, { accessories: CompanionAccessory[] }>> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const skuParams = skus.map(s => `skus=${encodeURIComponent(s)}`).join('&');
    const response = await httpClient.get<{ data: { products: Record<string, { accessories: CompanionAccessory[] }> } }>(
      `api/v1/solution/getHermesCompanions?${skuParams}&country=${encodeURIComponent(country)}&language=${encodeURIComponent(language)}`
    );
    return response.data.data.products;
  } catch (error) {
    return {};
  }
};

// ===============================================================================
// Captcha
// ===============================================================================
export const newCaptchaId = async (): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.get<any>(`captcha/new`);
    return response.data.captchaId;
  } catch (error) {
    return null;
  }
};

export const validateCaptcha = async (payload: { captchaId: string; captchaSolution: string }): Promise<boolean> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  const response = await httpClient.post<any>(
    "captcha/validate",
    payload
  );
  return response.data.isValid;
};

// ===============================================================================
// Cache
// ===============================================================================
export const refreshCache = async (): Promise<any> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.post<any>(
      `api/v1/internal/cache/refresh-cache`,
      {}
    );
    return response.data;
  } catch (error) {
    return null;
  }
};

// ===============================================================================
// Publisher queue / Ledger
// ===============================================================================
export interface SkippedRun {
  run_id: number;
  run_name: string;
  source_jira_key?: string;
  skip_reason: string;
  skip_stage: string;
}

export interface RetryFlags {
  confluence: boolean;
  jira: boolean;
}

export interface SolutionRejection {
  reason: string;
  message?: string;
  device_id?: string;
  device_name?: string;
  product_id?: string;
  product_name?: string;
  error?: string;
}

export interface PublisherBatchResult {
  batch_id: string;
  op: string;
  status: string;
  solutions: PublisherSolution[];
  oids?: string[];
  modified?: number;
  rejections?: SolutionRejection[];
  error_msg?: string;
  attempted_at?: string;
  unknown_reason_count?: number;
}

export interface QueueSolutionDiagnostic {
  product_id: string;
  device_id: string;
  unknown_reason_count: number;
}

export interface QueueItem {
  id: string;
  job_code: string;
  status: string;
  created_at: string;
  updated_at?: string;
  jira_ticket_key?: string;
  confluence_url?: string;
  retry_flags?: RetryFlags;
  source_test_run_ids?: number[];
  source_jira_keys?: string[];
  skipped_runs?: SkippedRun[];
  batches?: PublisherBatchResult[];
  solution_diagnostics?: QueueSolutionDiagnostic[];
  unknown_reason_count?: number;
}

export const listRecentQueueItems = async (limit = 50): Promise<QueueItem[]> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.get<{ data: { jobs: QueueItem[]; count: number } }>(
      `api/v1/publisherQueue/list?limit=${limit}`
    );
    const jobs = response?.data?.data?.jobs;
    return Array.isArray(jobs) ? jobs : [];
  } catch {
    return [];
  }
};

export const getQueueItemByJobCode = async (jobCode: string): Promise<QueueItem | null> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.get<{ data: { job: QueueItem } }>(
      `api/v1/publisherQueue/getByJobCode/${encodeURIComponent(jobCode)}`
    );
    return response?.data?.data?.job ?? null;
  } catch {
    return null;
  }
};

export const retryBatch = async (
  queueItemId: string,
  batchId: string,
  solutions: PublisherSolution[]
): Promise<{ ok: boolean; data?: any; error?: string }> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.post<any>(
      `api/v1/publisherQueue/retryBatch/${queueItemId}/${batchId}`,
      { solutions }
    );
    return { ok: true, data: response.data };
  } catch (err: any) {
    return { ok: false, error: err?.response?.data?.message ?? err?.message ?? 'Unknown error' };
  }
};

export const executeRollback = async (
  jobCode: string
): Promise<{ ok: boolean; data?: any; error?: string }> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.post<any>(
      `api/v1/ledger/executeRollback?jobCode=${encodeURIComponent(jobCode)}`,
      {}
    );
    return { ok: true, data: response.data };
  } catch (err: any) {
    return { ok: false, error: err?.response?.data?.message ?? err?.message ?? 'Unknown error' };
  }
};

// ===============================================================================
// Miscellaneous / Other
// ===============================================================================

export interface SkuRecommendStatus {
  SKU: string;
  Recommend: boolean;
  GeneralAvailabilityDate: string;
}

export interface SkuRecommendStatusResponse {
  ResultData: SkuRecommendStatus[];
}

export const getSkuRecommendStatus = async (): Promise<SkuRecommendStatusResponse> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.get<{
      data: { results: SkuRecommendStatus[] };
    }>(`api/v1/device/getSkuRecommendStatus`);
    return {
      ResultData: response.data.data.results,
    };
  } catch (error) {
    return { ResultData: [] };
  }
};

export interface ItemNumberLocale {
  CULTURE_CODES: string;
  ITEM_NUMBER: string;
};

export const getItemLocalesFromPDB = async (
  itemNumber: string
): Promise<ItemNumberLocale[]> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  const url = `api/v1/device/pdbItemLocales?itemNumber=${encodeURIComponent(itemNumber)}`;

  try {
    const response = await httpClient.get<{ data: { data: ItemNumberLocale[] } }>(url);
    return response.data.data.data;
  } catch (error) {
    return [];
  }
};

export interface ObjectHistoryResponse {
  history: any[] | Record<string, any[]>;
}

export interface CopySolutionsResponse {
  count: number;
  message: string;
}

export const getObjectHistory = async (
  objectIds: string[],
  startDate?: string,
  endDate?: string,
  type?: string
): Promise<ObjectHistoryResponse> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  const query = objectIds.map(id => `oid=${encodeURIComponent(id)}`).join("&");
  let url = `api/v1/history/getObjectHistory?${query}`;

  if (startDate) {
    url += `&startDate=${encodeURIComponent(startDate)}`;
  }
  if (endDate) {
    url += `&endDate=${encodeURIComponent(endDate)}`;
  }
  if (type) {
    url += `&type=${encodeURIComponent(type)}`;
  }

  const response = await httpClient.get<{ data: ObjectHistoryResponse }>(url);
  return response.data.data;
};

export const generateUrl = async (
  solution: SolutionAssociation,
  country: string,
  language: string
): Promise<string | null> => {
  const sku = extractSkuFromSolution(solution);
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.post<{ data: { url: string } }>(
      `api/v1/solution/getURL`,
      {
        skus: [sku],
        country: country,
        language: language,
      }
    );
    return response.data.data.url;
  } catch (error) {
    return null;
  }
};

export const getShopUrl = async (
  country: string,
  language: string
): Promise<string | null> => {
  const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
  try {
    const response = await httpClient.get<{ data: { url: string } }>(
      `api/v1/solution/shopURL?country=${country}&language=${language}`
    );
    return response.data.data.url;
  } catch (error) {
    return null;
  }
};

// export const getBase64ImageFromUrl = async (
//   urls: string[]
// ): Promise<{ [key: string]: string } | null> => {
//   const httpClient = HttpClient.getInstance(window.env.SERVER_URL);
//   const query = urls.map((url) => `url=${url}`).join("&");
//   try {
//     const response = await httpClient.get<{
//       data: { base64Images: { [key: string]: string } };
//     }>(`api/v1/device/getBase64ImageFromUrl?${query}`);
//     return response.data.data.base64Images;
//   } catch (error) {
//     return {};
//   }
// };